import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  BusinessRounded as BusinessIcon,
  KeyboardArrowDownRounded as ArrowIcon,
  CheckRounded as CheckIcon,
} from "@mui/icons-material";
import theme from "../../theme";
import { switchAccount } from "../../lib/activeAccount";

export function AccountSwitcher({ accounts, accountInfo }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const location = useLocation();
  const canSwitch = location.pathname === "/Home";

  // Only internal users with several accounts see the switcher
  if (!accountInfo || accounts.length < 2) return null;

  const handleSelect = (id) => {
    setAnchorEl(null);
    if (id !== accountInfo.accountId) switchAccount(id);
  };

  return (
    <>
      <Button
        onClick={canSwitch ? (e) => setAnchorEl(e.currentTarget) : undefined}
        aria-label="Switch account"
        endIcon={
            canSwitch ? (
                <ArrowIcon
                sx={{
                    transition: "transform 0.2s",
                    transform: open ? "rotate(180deg)" : "none",
                }}
                />
            ) : null
        }
        startIcon={<BusinessIcon />}
        sx={{
            mr: 2,
            px: 1.5,
            py: 0.5,
            maxWidth: 260,
            textTransform: "none",
            borderRadius: theme.borderRadius.full,
            border: `1px solid ${theme.palette.primary.brandSecondary}`,
            backgroundColor: `${theme.palette.primary.brandSecondary}14`,
            color: theme.palette.primary.main,

            // Clickable only on Home
            cursor: canSwitch ? "pointer" : "default",

            "&:hover": canSwitch
                ? {
                    backgroundColor: `${theme.palette.primary.brandSecondary}29`,
                    boxShadow: `2px 2px 8px ${theme.palette.primary.brandSecondary}45`,
                }
                : {
                    backgroundColor: `${theme.palette.primary.brandSecondary}14`,
                    boxShadow: "none",
                },
        }}
      >
        <Box sx={{ textAlign: "left", minWidth: 0, lineHeight: 1.1 }}>
          <Typography
            variant="caption"
            sx={{
              display: { xs: "none", sm: "block" },
              fontSize: "10px",
              lineHeight: 1.2,
              color: theme.palette.primary.main60,
            }}
          >
            Viewing account
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            {accountInfo.accountName}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: theme.borderRadius.medium,
              boxShadow: theme.customShadows.larger,
            },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: "block", color: theme.palette.primary.main60 }}
        >
          Switch account
        </Typography>
        {accounts.map((a) => {
          const isActive = a.accountId === accountInfo.accountId;
          return (
            <MenuItem
              key={a.accountId}
              onClick={() => handleSelect(a.accountId)}
              selected={isActive}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isActive && (
                  <CheckIcon
                    fontSize="small"
                    sx={{ color: theme.palette.primary.brandSecondary }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={a.accountName}
                slotProps={{
                  primary: { variant: "body2", fontWeight: isActive ? 600 : 400 },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}